import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronRight, Rows3, Columns3, Sigma, X, GripVertical, Filter as FilterIcon, Plus, Search, Download, ChevronsDownUp, ChevronsUpDown } from "lucide-react";
import api from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatValue } from "@/lib/chartTheme";
import { buildCondEval, condStyle } from "@/lib/condFormat";
import { CondIcon } from "@/components/bi/CondIcon";
import { splitPivotDims, splitPivotDimObjects, computeClientLevels, buildPivotModel, PSEP, dimOutName } from "@/lib/pivotEngine";
import { fontCss } from "@/components/bi/FontStyleControls";
import { t } from "@/lib/i18n";

import { ColumnFilterPopover } from "@/components/bi/ColumnFilterPopover";
import { matchesColumnFilter, isFilterActive, applyColumnFilters, describeFilter } from "@/lib/columnFilter";

const EMPTY_TOKENS = { dash: "\u2014", zero: "0", blank: "" };
const EMPTY_MODES = { blank: "", zero: "0", dash: "\u2014", na: "N/A" };

// Item 84 / 26: field filter matching lives in lib/columnFilter (shared with Table + DataGrid)
const ffMatch = matchesColumnFilter;
const ffActive = isFilterActive;

function hexToRgba(hex, a) {
  const m = /^#?([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i.exec(hex || "#2B4EFF");
  if (!m) return undefined;
  return `rgba(${parseInt(m[1], 16)}, ${parseInt(m[2], 16)}, ${parseInt(m[3], 16)}, ${a})`;
}

// Enterprise pivot (items 78-98 + Batch 8): multi row/col dims, expand/collapse, subtotals,
// grand totals, % displays, heatmap, dual engine — now with Classic (tabular) layout,
// repeating labels, a drag-and-drop grouping bar and windowed row virtualization.
export function PivotTable({ data, config = {}, onConfigChange = null, datasetId = null, publicToken = null, onCellDrill = null, onCellDrillthrough = null, drillTrigger = "right_click" }) {
  const opts = config.options?.pivotOpts || {};
  const loc = config.options?.locale || undefined;
  const L_GRAND = t("grand_total");
  const L_TOTAL = t("total");
  const valF = opts.valuesFmt || {};
  const emptyTok = opts.emptyMode === "custom"
    ? (valF.nullDisplay ?? "")
    : opts.emptyMode && EMPTY_MODES[opts.emptyMode] !== undefined
      ? EMPTY_MODES[opts.emptyMode]
      : (valF.nullDisplay !== undefined && valF.nullDisplay !== "") ? valF.nullDisplay : EMPTY_TOKENS[config.options?.emptyAs || "dash"];
  const columns = data.columns || [];
  const { rowDims, colDims } = splitPivotDims(config, columns);
  const measMeta = {}; (data.meta?.measures || []).forEach((m) => { measMeta[m.key] = m; });
  const dimMeta = {}; (data.meta?.dimensions || []).forEach((d) => { dimMeta[d.key] = d; });
  const dimSet = new Set([...rowDims, ...colDims]);
  const measureKeys = columns.filter((c) => !dimSet.has(c));
  const nMeas = measureKeys.length;
  const R = rowDims.length, C = colDims.length;
  const tabular = opts.layout === "tabular";
  const classic = opts.layout === "classic" || tabular;
  const repeatLabels = classic && !!opts.repeatLabels;
  const [q, setQ] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);

  // Item 26: header filters — Builder persists on pivotOpts; the viewer keeps an in-session copy.
  const [localFF, setLocalFF] = useState(null);
  const [localMF, setLocalMF] = useState(null);
  const effFF = localFF ?? (opts.fieldFilters || {});
  const effMF = localMF ?? (opts.measureFilters || {});
  const configKey = JSON.stringify({ p: opts, d: config.dimensions, m: config.measures, lf: localFF, lm: localMF });

  // Item 198.13 + 84: field filters, pivot-level Top N + value filters
  const rowsForModel = useMemo(() => {
    let rows = data.rows || [];
    const ffs = effFF;
    const ffOn = Object.entries(ffs).some(([, ff]) => ffActive(ff));
    if (ffOn) {
      const ents = Object.entries(ffs).filter(([, ff]) => ffActive(ff));
      rows = rows.filter((r) => ents.every(([f, ff]) => ffMatch(ff, r[f])));
    }
    const mfOn = Object.entries(effMF).some(([, ff]) => ffActive(ff));
    if (mfOn) rows = applyColumnFilters(rows, effMF);
    const vf = opts.valueFilter, tn = opts.topN;
    const vfOn = vf?.measure && vf.op && vf.value !== "" && vf.value != null;
    const tnOn = tn && Number(tn.n) > 0 && tn.measure;
    if ((!vfOn && !tnOn) || !rowDims.length) return { rows, filtered: ffOn || mfOn };
    const g1 = rowDims[0];
    const totals = {};
    const addM = (m, k, v) => { totals[m] = totals[m] || {}; totals[m][k] = (totals[m][k] || 0) + (Number(v) || 0); };
    rows.forEach((r) => {
      if (vfOn) addM(vf.measure, r[g1], r[vf.measure]);
      if (tnOn) addM(tn.measure, r[g1], r[tn.measure]);
    });
    let keep = null;
    if (tnOn) {
      keep = new Set(Object.entries(totals[tn.measure] || {})
        .sort((a, b) => (tn.dir === "asc" ? a[1] - b[1] : b[1] - a[1]))
        .slice(0, Number(tn.n)).map(([k]) => k));
    }
    const cmp = { gt: (a, b) => a > b, gte: (a, b) => a >= b, lt: (a, b) => a < b, lte: (a, b) => a <= b, eq: (a, b) => a === b }[vf?.op];
    rows = rows.filter((r) => {
      const k = r[g1];
      if (keep && !keep.has(String(k)) && !keep.has(k)) return false;
      if (vfOn && cmp && !cmp((totals[vf.measure] || {})[k] ?? 0, Number(vf.value))) return false;
      return true;
    });
    return { rows, filtered: true };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, configKey]);

  const model = useMemo(() => {
    if (!R || !nMeas) return null;
    const aggByKey = {};
    (config.measures || []).forEach((m) => { const k = m.key || m.alias || `${m.agg || "sum"}_${m.field}`; aggByKey[k] = m.agg || "sum"; });
    const levels = (!rowsForModel.filtered && data.levels)
      ? data.levels
      : computeClientLevels(rowsForModel.rows, rowDims, colDims, measureKeys, aggByKey);
    return buildPivotModel({ levels, rowDims, colDims, measureKeys, sortBy: opts.sortBy || "label", sortDir: opts.sortDir || "asc", sortMeasure: opts.sortMeasure, levelSort: opts.levelSort, colLevelSort: opts.colLevelSort });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, configKey]);

  const stateKey = `${rowDims.join("|")}::${opts.collapsed ? 1 : 0}`;
  const [collapsedState, setCollapsedState] = useState({ key: null, set: new Set() });
  const collapsed = collapsedState.key === stateKey
    ? collapsedState.set
    : (opts.collapsed && model ? new Set(model.groupKeys) : new Set());
  const toggle = (key) => {
    const next = new Set(collapsed);
    if (next.has(key)) next.delete(key); else next.add(key);
    setCollapsedState({ key: stateKey, set: next });
  };
  // Item 198.19: expand/collapse all + one level at a time
  const expandAll = () => setCollapsedState({ key: stateKey, set: new Set() });
  const collapseAll = () => setCollapsedState({ key: stateKey, set: new Set(model?.groupKeys || []) });
  const keyDepth = (k) => k.split(PSEP).length;
  const collapseOneLevel = () => {
    const expanded = (model?.groupKeys || []).filter((k) => !collapsed.has(k));
    if (!expanded.length) return;
    const maxD = Math.max(...expanded.map(keyDepth));
    const next = new Set(collapsed);
    expanded.filter((k) => keyDepth(k) === maxD).forEach((k) => next.add(k));
    setCollapsedState({ key: stateKey, set: next });
  };
  const expandOneLevel = () => {
    const cur = [...collapsed];
    if (!cur.length) return;
    const minD = Math.min(...cur.map(keyDepth));
    const next = new Set(cur.filter((k) => keyDepth(k) !== minD));
    setCollapsedState({ key: stateKey, set: next });
  };

  const ev = buildCondEval(config.options?.condRules, data.rows || []);

  // Item 80: measure-column sort (header click) + interactive column resize
  const patchPivotOpts = onConfigChange ? (patch) => onConfigChange({ options: { ...(config.options || {}), pivotOpts: { ...opts, ...patch } } }) : null;
  const sortByMeasure = patchPivotOpts ? (m) => {
    const active = opts.sortBy === "value" && (opts.sortMeasure || (config.measures || [])[0]) === m;
    patchPivotOpts({ sortBy: "value", sortMeasure: m, sortDir: active && opts.sortDir !== "desc" ? "desc" : "asc" });
  } : null;
  const sortIndicator = (m) => (opts.sortBy === "value" && opts.sortMeasure === m) ? (opts.sortDir === "desc" ? " ↓" : " ↑") : "";
  const setFieldFilter = (f, ff) => {
    const next = { ...effFF }; if (ff) next[f] = ff; else delete next[f];
    setLocalFF(next); if (patchPivotOpts) patchPivotOpts({ fieldFilters: next });
  };
  const setMeasureFilter = (m, ff) => {
    const next = { ...effMF }; if (ff) next[m] = ff; else delete next[m];
    setLocalMF(next); if (patchPivotOpts) patchPivotOpts({ measureFilters: next });
  };
  const clearAllHeaderFilters = () => { setLocalFF({}); setLocalMF({}); if (patchPivotOpts) patchPivotOpts({ fieldFilters: {}, measureFilters: {} }); };
  const rawValuesOf = (f) => (data.rows || []).map((r) => r[f]);
  const headerFilterFor = (f, kind = null, extraCls = "") => opts.headerFilters === false ? null : (
    <ColumnFilterPopover column={f} label={labelFor(f)} values={rawValuesOf(f)} kind={kind} testid={`pivot-hf-${f}`} iconClass={extraCls}
      filter={kind === "number" ? effMF[f] : effFF[f]} onApply={(ff) => (kind === "number" ? setMeasureFilter(f, ff) : setFieldFilter(f, ff))} />
  );
  const activeHeaderFilters = [...Object.keys(effFF).filter((f) => ffActive(effFF[f])).map((f) => ({ f, ff: effFF[f], kind: "text" })),
    ...Object.keys(effMF).filter((f) => ffActive(effMF[f])).map((f) => ({ f, ff: effMF[f], kind: "number" }))];
  const startResize = patchPivotOpts ? (m, e) => {
    e.preventDefault(); e.stopPropagation();
    const th = e.currentTarget.closest("th");
    const startX = e.clientX, startW = th.offsetWidth;
    let w = startW;
    const move = (ev2) => { w = Math.max(40, startW + ev2.clientX - startX); th.style.width = `${w}px`; th.style.minWidth = `${w}px`; };
    const up = () => {
      window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up);
      patchPivotOpts({ colFmt: { ...(opts.colFmt || {}), [m]: { ...((opts.colFmt || {})[m] || {}), width: w } } });
    };
    window.addEventListener("mousemove", move); window.addEventListener("mouseup", up);
  } : null;

  // virtualization plumbing (must run before any early return)
  const scrollRef = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewH, setViewH] = useState(420);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => setScrollTop(el.scrollTop);
    const ro = new ResizeObserver(() => setViewH(el.clientHeight));
    el.addEventListener("scroll", onScroll, { passive: true });
    ro.observe(el);
    setViewH(el.clientHeight);
    return () => { el.removeEventListener("scroll", onScroll); ro.disconnect(); };
  }, [model]);

  const groupingBar = onConfigChange && opts.groupingBar !== false
    ? <GroupingBar config={config} onConfigChange={onConfigChange} dimMeta={dimMeta} measMeta={measMeta} datasetId={datasetId}
        rows={data.rows || []} fieldFilters={effFF} onFieldFilter={setFieldFilter} />
    : null;

  if (!model) {
    return (
      <div className="h-full w-full flex flex-col">
        {groupingBar}
        <div className="flex-1 flex items-center justify-center">
          <div className="text-xs text-[color:var(--ink-3)] text-center px-4">
            Pivot needs at least one <b>Row</b> dimension and one measure.<br />
            Use the Row/Col toggle next to each dimension to assign axes.
          </div>
        </div>
      </div>
    );
  }

  const rowSubtotals = opts.rowSubtotals !== false;
  const colSubtotals = !!opts.colSubtotals;
  const grandRow = opts.grandRow !== false;
  const grandCol = opts.grandCol !== false && C > 0;
  const valueAs = opts.valueAs || "raw";
  const heat = !!opts.heatmap;
  const wrapCls = opts.wrap ? "whitespace-normal break-words" : "whitespace-nowrap";
  const padCls = opts.dense ? "px-2 py-0.5" : "px-3 py-1.5";
  const freezeCol = opts.freezeFirstCol !== false;
  const colMinW = Number(opts.colMinWidth) > 0 ? { minWidth: Number(opts.colMinWidth) } : undefined;
  const striped = !!opts.striped;
  const labelFor = (c) => measMeta[c]?.label || dimMeta[c]?.label || String(c).replace(/_/g, " ");
  const labelCols = classic ? R : 1;

  // ---- column layout ----
  const leafCols = [];
  const walkCols = (prefix, depth, tuples) => {
    if (depth === C) { leafCols.push({ cPath: prefix, kind: "leaf" }); return; }
    let i = 0;
    while (i < tuples.length) {
      const val = tuples[i][depth];
      const sub = [];
      while (i < tuples.length && tuples[i][depth] === val) { sub.push(tuples[i]); i += 1; }
      walkCols([...prefix, val], depth + 1, sub);
      if (colSubtotals && depth + 1 < C) leafCols.push({ cPath: [...prefix, val], kind: "subtotal" });
    }
  };
  walkCols([], 0, model.colTuples);
  if (grandCol) leafCols.push({ cPath: [], kind: "grand" });

  const measRow = C > 0 && nMeas >= 1;
  const headerRows = [];
  for (let l = 0; l < C; l++) {
    const cells = [];
    let i = 0;
    while (i < leafCols.length) {
      const lc = leafCols[i];
      if (lc.cPath.length < l) { i += 1; continue; }
      if (lc.cPath.length === l) {
        cells.push({
          label: lc.kind === "grand" ? L_GRAND : `${lc.cPath[l - 1] ?? ""} ${L_TOTAL}`,
          colSpan: nMeas, rowSpan: C - l, total: true, key: `t${i}`,
        });
        i += 1; continue;
      }
      const key = lc.cPath.slice(0, l + 1).join(PSEP);
      const start = i;
      let span = 0;
      while (i < leafCols.length && leafCols[i].kind !== "grand" && leafCols[i].cPath.length > l
        && leafCols[i].cPath.slice(0, l + 1).join(PSEP) === key) { span += nMeas; i += 1; }
      cells.push({ label: lc.cPath[l], colSpan: span, rowSpan: 1, key: `g${l}-${start}` });
    }
    headerRows.push(cells);
  }

  // ---- body rows ----
  const bodyRows = [];
  const pushNode = (node, depth) => {
    const isLeaf = depth === R;
    const key = node.path.join(PSEP);
    if (tabular && !isLeaf) {
      // Tabular (Excel) layout: no group header rows — children first, then a subtotal row.
      node.children.forEach((ch) => pushNode(ch, depth + 1));
      if (rowSubtotals) bodyRows.push({ kind: "subtotal", path: node.path, depth });
      return;
    }
    const isCollapsed = !isLeaf && !q && collapsed.has(key);
    bodyRows.push({ kind: isLeaf ? "leaf" : "group", path: node.path, depth, collapsed: isCollapsed });
    if (!isLeaf && !isCollapsed) node.children.forEach((ch) => pushNode(ch, depth + 1));
  };
  model.rowTree.forEach((n) => pushNode(n, 1));
  if (grandRow) bodyRows.push({ kind: "grand", path: [], depth: 0 });

  // Item 198.14: instant search across row labels (forces expansion while searching)
  let visibleRows = bodyRows;
  if (q) {
    const ql = q.toLowerCase();
    const matches = (p) => p.some((seg) => String(seg ?? "").toLowerCase().includes(ql));
    const keepGroups = new Set();
    bodyRows.forEach((r) => {
      if (r.kind === "leaf" && matches(r.path)) {
        for (let d = 1; d < r.path.length; d++) keepGroups.add(r.path.slice(0, d).join(PSEP));
      }
    });
    visibleRows = bodyRows.filter((r) => {
      if (r.kind === "grand") return true;
      if (r.kind === "leaf") return matches(r.path);
      return keepGroups.has(r.path.join(PSEP)) || matches(r.path);
    });
  }

  const heatRange = {};
  if (heat) {
    measureKeys.forEach((m) => {
      let lo = Infinity, hi = -Infinity;
      (data.rows || []).forEach((r) => { const v = Number(r[m]); if (Number.isFinite(v)) { if (v < lo) lo = v; if (v > hi) hi = v; } });
      if (lo !== Infinity && hi > lo) heatRange[m] = [lo, hi];
    });
  }

  const { rowObjs: drillRowObjs, colObjs: drillColObjs } = splitPivotDimObjects(config);
  const cellFilters = (row, lc) => {
    const fs = [];
    row.path.forEach((val, i) => {
      const d = drillRowObjs[i];
      if (d?.field && (!d.date_part || d.date_part === "none")) fs.push({ field: d.field, op: "eq", value: val });
    });
    (lc.cPath || []).forEach((val, i) => {
      const d = drillColObjs[i];
      if (d?.field && (!d.date_part || d.date_part === "none")) fs.push({ field: d.field, op: "eq", value: val });
    });
    return fs;
  };
  const drillCell = onCellDrill ? (row, lc) => onCellDrill(cellFilters(row, lc)) : null;
  // Item 13: cross-dashboard drillthrough on the configured trigger (right / double / single click)
  const dtHandlers = onCellDrillthrough ? (row, lc) => {
    const h = (e) => { if (drillTrigger === "right_click") e.preventDefault(); onCellDrillthrough(cellFilters(row, lc), e); };
    return drillTrigger === "click" ? { onClick: h } : drillTrigger === "double_click" ? { onDoubleClick: h } : { onContextMenu: h };
  } : () => ({});

  // ---- Item 27: header / values / column / gridline / alt-row / totals formatting ----
  const hdrF = opts.headerFmt || {};
  const altF = opts.altRows || {};
  const glF = opts.gridlines || {};
  const subF = opts.subtotalFmt || {};
  const grandF = opts.grandFmt || {};
  const hdrStyle = { ...fontCss(hdrF), ...(hdrF.bg ? { backgroundColor: hdrF.bg } : {}) };
  const valStyle = (() => { const s = { ...fontCss(valF) }; if (valF.padding !== undefined && valF.padding !== "") s.padding = `${valF.padding}px`; return s; })();
  const colFOf = (c) => (opts.colFmt || {})[c] || {};
  const colStyleOf = (c) => {
    const f = colFOf(c);
    const s = { ...fontCss(f) };
    if (f.bg) s.backgroundColor = f.bg;
    if (f.width) { s.width = `${f.width}px`; s.minWidth = `${f.width}px`; }
    if (f.wrap) { s.whiteSpace = "normal"; s.wordBreak = "break-word"; }
    return s;
  };
  const gridVStyle = glF.v ? { borderLeft: `1px solid ${glF.vColor || "var(--glass-border)"}` } : null;
  // Item 27: merged (rowSpan) label cells need their own borders — tr borders don't reach them.
  const labelGrid = {
    ...(glF.v ? { borderRight: `1px solid ${glF.vColor || "var(--glass-border)"}` } : {}),
    ...(glF.h === false ? { borderTop: "none", borderBottom: "none" } : { borderBottom: `1px solid ${glF.hColor || "var(--glass-border)"}` }),
  };
  const hLineStyle = glF.h === false ? { borderTop: "none" } : glF.hColor ? { borderTopColor: glF.hColor } : null;
  const dataBars = opts.dataBars || {};
  const barRange = {};
  measureKeys.forEach((m) => {
    if (!dataBars[m]?.on) return;
    let lo = Infinity, hi = -Infinity;
    (rowsForModel.rows || []).forEach((r) => { const v = Number(r[m]); if (Number.isFinite(v)) { if (v < lo) lo = v; if (v > hi) hi = v; } });
    if (lo !== Infinity) barRange[m] = [Math.min(0, lo), hi];
  });
  const kindStyle = (row) => row.kind === "grand"
    ? { ...(grandF.bg ? { backgroundColor: grandF.bg } : {}), ...(grandF.color ? { color: grandF.color } : {}), ...(grandF.bold === false ? { fontWeight: 500 } : {}) }
    : (row.kind === "subtotal" || row.kind === "group")
      ? { ...(subF.bg ? { backgroundColor: subF.bg } : {}), ...(subF.color ? { color: subF.color } : {}), ...(subF.bold === false ? { fontWeight: 500 } : {}) }
      : null;
  const altStyle = (row, ri) => (altF.enabled && row.kind === "leaf")
    ? { ...((ri % 2 === 1 ? altF.oddBg : altF.evenBg) ? { backgroundColor: ri % 2 === 1 ? altF.oddBg : altF.evenBg } : {}), ...(altF.textColor ? { color: altF.textColor } : {}) }
    : null;
  const rowExtra = (row, ri) => {
    const s = { ...(kindStyle(row) || {}), ...(altStyle(row, ri) || {}) };
    return Object.keys(s).length ? s : null;
  };
  const pivotUid = `pvt-h-${String(opts.hoverColor || "none").replace(/[^a-zA-Z0-9]/g, "")}`;

  // ---- Item 6: Show Values As — Difference / Running total over the displayed sequence ----
  const seqRowKey = (row) => `${row.kind}:${row.path.join(PSEP)}`;
  const seqColKey = (lc) => `${lc.kind}:${(lc.cPath || []).join(PSEP)}`;
  const derivedSeq = {};
  if (valueAs === "diff" || valueAs === "running") {
    const across = opts.valueAsDir === "across";
    const leafRows = visibleRows.filter((r) => r.kind === "leaf");
    const leafOnlyCols = leafCols.filter((lc) => lc.kind === "leaf");
    const walk = (cells, getValue, setValue) => {
      let prev = null, run = 0;
      cells.forEach((cell) => {
        const v = getValue(cell);
        if (valueAs === "diff") setValue(cell, v == null || prev == null ? null : v - prev);
        else {
          if (v != null) run += v;
          setValue(cell, v == null ? null : run);
        }
        prev = v;
      });
    };
    if (across) {
      leafRows.forEach((row) => measureKeys.forEach((m) => walk(
        leafOnlyCols,
        (lc) => model.getCell(row.path, lc.cPath, m),
        (lc, val) => { derivedSeq[`${seqRowKey(row)}|${seqColKey(lc)}|${m}`] = val; },
      )));
    } else {
      leafOnlyCols.forEach((lc) => measureKeys.forEach((m) => walk(
        leafRows,
        (row) => model.getCell(row.path, lc.cPath, m),
        (row, val) => { derivedSeq[`${seqRowKey(row)}|${seqColKey(lc)}|${m}`] = val; },
      )));
    }
  }

  const renderValueTd = (row, lc, m, key, showVals, extra) => {
    if (!showVals) return <td key={key} className={padCls} style={{ ...(gridVStyle || {}), ...(extra || {}) }} />;
    const totalish = row.kind !== "leaf" || lc.kind !== "leaf";
    const v = model.getCell(row.path, lc.cPath, m);
    const decimalsOf = () => {
      const d = colFOf(m).decimals !== undefined ? colFOf(m).decimals : valF.decimals;
      return Number.isFinite(Number(d)) ? Number(d) : 1;
    };
    let disp;
    if (valueAs === "diff" || valueAs === "running") {
      // Item 6: difference / running total follow the displayed sequence of leaf cells.
      const dv = totalish ? undefined : derivedSeq[`${seqRowKey(row)}|${seqColKey(lc)}|${m}`];
      disp = dv == null ? emptyTok
        : formatValue(dv, colFOf(m).decimals !== undefined ? { ...(measMeta[m]?.format || {}), decimals: colFOf(m).decimals } : measMeta[m]?.format, loc);
    } else if (v == null) disp = emptyTok;
    else if (valueAs !== "raw") {
      const denom = valueAs === "pct_row" ? model.getCell(row.path, [], m)
        : valueAs === "pct_col" ? model.getCell([], lc.cPath, m)
          : model.getCell([], [], m);
      disp = denom ? `${((v / denom) * 100).toFixed(decimalsOf())}%` : emptyTok;
    } else disp = formatValue(v, colFOf(m).decimals !== undefined ? { ...(measMeta[m]?.format || {}), decimals: colFOf(m).decimals } : measMeta[m]?.format, loc);
    const isBase = row.kind === "leaf" && lc.kind === "leaf";
    const fx = isBase && ev && v != null ? (ev.cell({ [m]: v }, m) || ev.cell({ value: v }, "value")) : null;
    let style = fx ? condStyle(fx) : undefined;
    if (!style && heat && isBase && v != null && heatRange[m]) {
      const [lo, hi] = heatRange[m];
      style = { backgroundColor: hexToRgba(opts.heatColor || "#2B4EFF", 0.06 + ((v - lo) / (hi - lo)) * 0.45) };
    }
    if (!style?.backgroundImage && dataBars[m]?.on && v != null && barRange[m] && (isBase || dataBars[m].totals)) {
      const [lo, hi] = barRange[m];
      const pct = hi > lo ? Math.max(2, Math.min(100, ((Number(v) - lo) / (hi - lo)) * 100)) : 100;
      style = { ...(style || {}), backgroundImage: `linear-gradient(90deg, ${hexToRgba(dataBars[m].color || "#2B4EFF", 0.3)} ${pct}%, transparent ${pct}%)` };
    }
    const merged = { ...valStyle, ...colStyleOf(m), ...(gridVStyle || {}), ...(extra || {}), ...(style || {}), ...(colMinW || {}) };
    const iconLeft = fx?.icon && fx.iconPos !== "right" && <CondIcon fx={fx} />;
    const iconRight = fx?.icon && fx.iconPos === "right" && <CondIcon fx={fx} />;
    return (
      <td key={key} data-testid="pivot-cell" style={Object.keys(merged).length ? merged : undefined}
        onDoubleClick={drillCell ? () => drillCell(row, lc) : undefined}
        {...dtHandlers(row, lc)}
        title={drillCell ? "Double-click to see the underlying rows" : undefined}
        className={`${padCls} text-right tabular-nums ${wrapCls} ${drillCell ? "cursor-zoom-in " : ""}` + (totalish ? "font-semibold text-[color:var(--ink)]" : "text-[color:var(--ink-2)]")}>
        {iconLeft}{fx?.iconPos === "only" ? null : disp}{iconRight}
      </td>
    );
  };

  // Classic layout: one column per row dimension; blank repeats unless repeatLabels
  const renderLabelCells = (row, ri, prevLeafPath) => {
    const isGrand = row.kind === "grand";
    const isGroup = row.kind === "group";
    const isSubtotal = row.kind === "subtotal";
    const extra = rowExtra(row, ri) || {};
    if (!classic) {
      const label = isGrand ? L_GRAND : row.path[row.path.length - 1];
      return (
        <td className={`${padCls} ${wrapCls} ${freezeCol ? "sticky left-0 z-[5]" : ""} ` + (isGrand ? "bg-[color:var(--surface-2)] font-semibold text-[color:var(--ink)]" : isGroup ? "bg-[color:var(--glass-bg)] font-semibold text-[color:var(--ink)]" : "bg-[color:var(--glass-bg)] font-medium text-[color:var(--ink-2)]")}
          style={{ paddingLeft: 12 + Math.max(0, row.depth - 1) * 16, ...labelGrid, ...extra }}>
          {isGroup && (
            <button data-testid="pivot-expand-btn" onClick={() => toggle(row.path.join(PSEP))} aria-expanded={!row.collapsed} aria-label={`${row.collapsed ? "Expand" : "Collapse"} ${label}`}
              className="inline-flex items-center justify-center w-4 h-4 mr-1 rounded hover:bg-[color:var(--surface-2)] align-[-2px]">
              {row.collapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
          )}
          {label}
        </td>
      );
    }
    if (isGrand) {
      return (
        <td colSpan={R} style={Object.keys(extra).length ? extra : undefined} className={`${padCls} ${wrapCls} ${freezeCol ? "sticky left-0 z-[5]" : ""} bg-[color:var(--surface-2)] font-semibold text-[color:var(--ink)]`}>
          {L_GRAND}
        </td>
      );
    }
    return Array.from({ length: R }, (_, d) => {
      const stickyCls = freezeCol && d === 0 ? " sticky left-0 z-[5]" : "";
      const dimStyle = { ...colStyleOf(rowDims[d]), ...(d > 0 && glF.v ? { borderLeft: `1px solid ${glF.vColor || "var(--glass-border)"}` } : {}), ...(d === R - 1 ? labelGrid : (glF.h === false ? { borderTop: "none" } : {})), ...extra };
      if (isSubtotal) {
        return (
          <td key={d} data-testid={d === row.depth - 1 ? "pivot-subtotal-cell" : undefined}
            style={Object.keys(dimStyle).length ? dimStyle : undefined}
            className={`${padCls} ${wrapCls} bg-[color:var(--glass-bg)] font-semibold text-[color:var(--ink)]` + stickyCls}>
            {d === row.depth - 1 ? `${row.path[d]} ${L_TOTAL}` : ""}
          </td>
        );
      }
      if (isGroup) {
        const own = d === row.depth - 1;
        return (
          <td key={d} style={Object.keys(dimStyle).length ? dimStyle : undefined} className={`${padCls} ${wrapCls} bg-[color:var(--glass-bg)] font-semibold text-[color:var(--ink)]` + stickyCls}>
            {own && (
              <>
                <button data-testid="pivot-expand-btn" onClick={() => toggle(row.path.join(PSEP))} aria-expanded={!row.collapsed} aria-label={`${row.collapsed ? "Expand" : "Collapse"} ${row.path[d]}`}
                  className="inline-flex items-center justify-center w-4 h-4 mr-1 rounded hover:bg-[color:var(--surface-2)] align-[-2px]">
                  {row.collapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                </button>
                {row.path[d]}{rowSubtotals && !row.collapsed ? ` ${L_TOTAL}` : ""}
              </>
            )}
          </td>
        );
      }
      // leaf: rowspan merged parents (item 80, repeat labels OFF), or repeated/blanked labels
      const spanMode = !repeatLabels && !virtual;
      if (spanMode && d < R - 1) {
        const keyOf = (p) => p.slice(0, d + 1).join(PSEP);
        const changed = !prevLeafPath || keyOf(prevLeafPath) !== keyOf(row.path);
        if (!changed) return null;
        let span = 1;
        for (let j = ri + 1; j < visibleRows.length; j++) {
          const nr = visibleRows[j];
          if (nr.kind !== "leaf" || keyOf(nr.path) !== keyOf(row.path)) break;
          span += 1;
        }
        return (
          <td key={d} rowSpan={span} data-testid={`pivot-classic-cell-${d}`}
            style={Object.keys(dimStyle).length ? dimStyle : undefined}
            className={`${padCls} ${wrapCls} align-top bg-[color:var(--glass-bg)] text-[color:var(--ink-2)]` + stickyCls}>
            {row.path[d]}
          </td>
        );
      }
      const show = repeatLabels || spanMode || d === R - 1 || !prevLeafPath || prevLeafPath[d] !== row.path[d] || prevLeafPath.slice(0, d).join(PSEP) !== row.path.slice(0, d).join(PSEP);
      return (
        <td key={d} data-testid={`pivot-classic-cell-${d}`}
          style={Object.keys(dimStyle).length ? dimStyle : undefined}
          className={`${padCls} ${wrapCls} bg-[color:var(--glass-bg)] ` + (d === R - 1 ? "font-medium text-[color:var(--ink-2)]" : "text-[color:var(--ink-2)]") + stickyCls}>
          {show ? row.path[d] : ""}
        </td>
      );
    });
  };

  const outline = ev?.widgetOutline();
  const cornerLabel = rowDims.map(labelFor).join(" / ");
  const headSticky = "sticky top-0 bg-[color:var(--surface-2)] z-10";

  // windowed rendering for large pivots
  const rowH = opts.dense ? 25 : 33;
  const virtual = !opts.wrap && visibleRows.length > 200;
  const vStart = virtual ? Math.max(0, Math.floor(scrollTop / rowH) - 12) : 0;
  const vEnd = virtual ? Math.min(visibleRows.length, Math.ceil((scrollTop + viewH) / rowH) + 12) : visibleRows.length;
  const totalTdCols = labelCols + leafCols.length * nMeas;

  // previous leaf path for classic label blanking (must look at full list, not window)
  const prevLeafOf = (idx) => {
    for (let i = idx - 1; i >= 0; i--) {
      if (visibleRows[i].kind === "leaf") return visibleRows[i].path;
      if (visibleRows[i].kind === "group") return null; // group header resets repetition
    }
    return null;
  };

  // Item 198.23: export the pivot exactly as laid out (fully expanded) to CSV/Excel.
  const exportPivotCsv = () => {
    const fullRows = [];
    const pushAll = (node, depth) => {
      const isLeaf = depth === R;
      if (tabular && !isLeaf) {
        node.children.forEach((ch) => pushAll(ch, depth + 1));
        if (rowSubtotals) fullRows.push({ kind: "subtotal", path: node.path, depth });
        return;
      }
      fullRows.push({ kind: isLeaf ? "leaf" : "group", path: node.path, depth, collapsed: false });
      if (!isLeaf) node.children.forEach((ch) => pushAll(ch, depth + 1));
    };
    model.rowTree.forEach((n) => pushAll(n, 1));
    if (grandRow) fullRows.push({ kind: "grand", path: [], depth: 0 });
    const headLbl = (lc) => lc.kind === "grand" ? L_GRAND : lc.kind === "subtotal" ? `${lc.cPath.join(" / ")} ${L_TOTAL}` : lc.cPath.join(" / ");
    const head = classic ? rowDims.map(labelFor) : [cornerLabel];
    leafCols.forEach((lc) => measureKeys.forEach((m) => head.push(C > 0 ? `${headLbl(lc)}${nMeas > 1 ? " · " + labelFor(m) : ""}` : labelFor(m))));
    const aoa = [head];
    fullRows.forEach((row) => {
      const isTotalRow = row.kind !== "leaf";
      let labels;
      if (classic) {
        labels = Array.from({ length: R }, (_, d) => {
          if (row.kind === "grand") return d === 0 ? L_GRAND : "";
          if (isTotalRow) return d === row.depth - 1 ? `${row.path[d]} ${L_TOTAL}` : (row.path[d] ?? "");
          return row.path[d] ?? "";
        });
      } else {
        labels = [row.kind === "grand" ? L_GRAND : `${"  ".repeat(Math.max(0, row.depth - 1))}${row.path[row.path.length - 1] ?? ""}${row.kind === "group" && rowSubtotals ? ` ${L_TOTAL}` : ""}`];
      }
      const vals = [];
      leafCols.forEach((lc) => measureKeys.forEach((m) => {
        if (row.kind === "group" && !rowSubtotals) { vals.push(""); return; }
        const v = model.getCell(row.path, lc.cPath, m);
        vals.push(v == null ? "" : v);
      }));
      aoa.push([...labels, ...vals]);
    });
    const csv = aoa.map((r) => r.map((c) => { const s = String(c ?? ""); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; }).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url; a.download = "pivot.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="h-full w-full flex flex-col min-h-0">
      {groupingBar}
      <div className="shrink-0 flex items-center gap-1 pb-1">
        {!tabular && R > 1 && (
          <>
            <button data-testid="pivot-expand-all" title="Expand all" aria-label="Expand all" onClick={expandAll} className="p-1 rounded text-[color:var(--ink-3)] hover:text-[color:var(--brand-ocean)] hover:bg-[color:var(--surface-2)]"><ChevronsUpDown className="w-3.5 h-3.5" /></button>
            <button data-testid="pivot-collapse-all" title="Collapse all" aria-label="Collapse all" onClick={collapseAll} className="p-1 rounded text-[color:var(--ink-3)] hover:text-[color:var(--brand-ocean)] hover:bg-[color:var(--surface-2)]"><ChevronsDownUp className="w-3.5 h-3.5" /></button>
            <button data-testid="pivot-expand-level" title="Expand one level" aria-label="Expand one level" onClick={expandOneLevel} className="px-1 py-0.5 rounded text-[10px] text-[color:var(--ink-3)] hover:text-[color:var(--brand-ocean)] hover:bg-[color:var(--surface-2)]">+1</button>
            <button data-testid="pivot-collapse-level" title="Collapse one level" aria-label="Collapse one level" onClick={collapseOneLevel} className="px-1 py-0.5 rounded text-[10px] text-[color:var(--ink-3)] hover:text-[color:var(--brand-ocean)] hover:bg-[color:var(--surface-2)]">−1</button>
          </>
        )}
        {activeHeaderFilters.length > 0 && (
          <span data-testid="pivot-filter-chips" className="flex items-center gap-1 flex-wrap text-[10px]">
            {activeHeaderFilters.map(({ f, ff, kind }) => (
              <span key={f} data-testid={`pivot-filter-chip-${f}`} className="inline-flex items-center gap-1 rounded-full border border-[color:var(--brand-ocean)]/40 bg-[color:var(--surface)] px-2 py-0.5 text-[color:var(--ink-2)]">
                <b className="font-medium">{labelFor(f)}</b> {describeFilter(ff, kind)}
                <button data-testid={`pivot-filter-chip-clear-${f}`} aria-label={`Clear filter on ${labelFor(f)}`} onClick={() => (kind === "number" ? setMeasureFilter(f, null) : setFieldFilter(f, null))} className="text-[color:var(--ink-3)] hover:text-[color:var(--danger)]"><X className="w-2.5 h-2.5" /></button>
              </span>
            ))}
            <button data-testid="pivot-filter-clear-all" onClick={clearAllHeaderFilters} className="text-[color:var(--ink-3)] hover:text-[color:var(--danger)]">Clear all</button>
          </span>
        )}
        <span className="flex-1" />
        {searchOpen ? (
          <span className="flex items-center gap-1">
            <Input data-testid="pivot-search-input" autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search rows…" className="h-6 w-40 text-[11px] bg-[color:var(--surface-2)]" />
            <button onClick={() => { setQ(""); setSearchOpen(false); }} aria-label="Close search" className="p-1 text-[color:var(--ink-3)] hover:text-[color:var(--danger)]"><X className="w-3.5 h-3.5" /></button>
          </span>
        ) : (
          <button data-testid="pivot-search-btn" title="Search row labels" aria-label="Search row labels" onClick={() => setSearchOpen(true)} className="p-1 rounded text-[color:var(--ink-3)] hover:text-[color:var(--brand-ocean)] hover:bg-[color:var(--surface-2)]"><Search className="w-3.5 h-3.5" /></button>
        )}
        <button data-testid="pivot-export-csv" title="Export pivot layout (CSV, opens in Excel)" aria-label="Export pivot layout (CSV, opens in Excel)" onClick={exportPivotCsv} className="p-1 rounded text-[color:var(--ink-3)] hover:text-[color:var(--brand-ocean)] hover:bg-[color:var(--surface-2)]"><Download className="w-3.5 h-3.5" /></button>
      </div>
      <div ref={scrollRef} data-testid="pivot-table" className={`flex-1 min-h-0 w-full overflow-auto overscroll-contain rounded-xl border border-[color:var(--glass-border)] bg-[color:var(--glass-bg)] ${pivotUid}`}
        style={outline ? { boxShadow: `inset 0 0 0 2px ${outline}` } : undefined}>
        {opts.hoverColor && <style>{`.${pivotUid} tbody tr:hover>td{background:${opts.hoverColor} !important}`}</style>}
        <table className="w-max min-w-full text-xs border-collapse" data-pivot-layout={tabular ? "tabular" : classic ? "classic" : "compact"}>
          <thead className={headSticky}>
            {C > 0 ? (
              <>
                {headerRows.map((cells, l) => (
                  <tr key={l}>
                    {l === 0 && (classic
                      ? rowDims.map((d, di) => (
                        <th key={d} rowSpan={C + (measRow ? 1 : 0)} style={hdrStyle}
                          className={`group/th text-left ${padCls} py-2 font-medium text-[color:var(--ink-2)] ${wrapCls} ${freezeCol && di === 0 ? "sticky left-0 z-20" : ""} bg-[color:var(--surface-2)]`}>
                          {labelFor(d)}{headerFilterFor(d)}
                        </th>
                      ))
                      : (
                        <th rowSpan={C + (measRow ? 1 : 0)} style={hdrStyle} className={`group/th text-left ${padCls} py-2 font-medium text-[color:var(--ink-2)] ${wrapCls} ${freezeCol ? "sticky left-0 z-20" : ""} bg-[color:var(--surface-2)]`}>
                          {cornerLabel}{rowDims.map((d) => <React.Fragment key={d}>{headerFilterFor(d)}</React.Fragment>)}
                        </th>
                      ))}
                    {cells.map((c, ci) => (
                      <th key={c.key} colSpan={c.colSpan} rowSpan={c.rowSpan} style={hdrStyle}
                        className={`group/th ${padCls} text-right whitespace-nowrap ` + (c.total ? "font-semibold text-[color:var(--ink)]" : "font-medium text-[color:var(--ink-2)]")}>
                        {ci === 0 && colDims[l] && headerFilterFor(colDims[l], null, "mr-1")}{c.label}
                      </th>
                    ))}
                  </tr>
                ))}
                {measRow && (
                  <tr>
                    {leafCols.map((lc, i) => measureKeys.map((m) => (
                      <th key={`${i}-${m}`} style={{ ...hdrStyle, ...(colFOf(m).width ? { width: colFOf(m).width, minWidth: colFOf(m).width } : {}) }} className="group/th relative px-3 py-1 text-right text-[10px] font-medium text-[color:var(--ink-3)] whitespace-nowrap">
                        {i === 0 && headerFilterFor(m, "number", "mr-0.5")}
                        <span data-testid={i === 0 ? `pivot-sort-${m}` : undefined} onClick={sortByMeasure ? () => sortByMeasure(m) : undefined}
                          className={sortByMeasure ? "cursor-pointer select-none hover:text-[color:var(--brand-ocean)]" : undefined}>{labelFor(m)}{sortIndicator(m)}</span>
                        {startResize && <span data-testid={i === 0 ? `pivot-resize-${m}` : undefined} onMouseDown={(e) => startResize(m, e)} onClick={(e) => e.stopPropagation()}
                          className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-[color:var(--brand-ocean)]/40" />}
                      </th>
                    )))}
                  </tr>
                )}
              </>
            ) : (
              <tr>
                {classic
                  ? rowDims.map((d, di) => (
                    <th key={d} style={hdrStyle} className={`group/th text-left ${padCls} py-2 font-medium text-[color:var(--ink-2)] ${freezeCol && di === 0 ? "sticky left-0 z-20" : ""} bg-[color:var(--surface-2)]`}>{labelFor(d)}{headerFilterFor(d)}</th>
                  ))
                  : <th style={hdrStyle} className={`group/th text-left ${padCls} py-2 font-medium text-[color:var(--ink-2)] ${freezeCol ? "sticky left-0 z-20" : ""} bg-[color:var(--surface-2)]`}>{cornerLabel}{rowDims.map((d) => <React.Fragment key={d}>{headerFilterFor(d)}</React.Fragment>)}</th>}
                {measureKeys.map((m) => (
                  <th key={m} style={{ ...hdrStyle, ...(colFOf(m).width ? { width: colFOf(m).width, minWidth: colFOf(m).width } : {}) }} className={`group/th relative ${padCls} py-2 text-right font-medium text-[color:var(--ink-2)] whitespace-nowrap`}>
                    {headerFilterFor(m, "number", "mr-0.5")}
                    <span data-testid={`pivot-sort-${m}`} onClick={sortByMeasure ? () => sortByMeasure(m) : undefined}
                      className={sortByMeasure ? "cursor-pointer select-none hover:text-[color:var(--brand-ocean)]" : undefined}>{labelFor(m)}{sortIndicator(m)}</span>
                    {startResize && <span data-testid={`pivot-resize-${m}`} onMouseDown={(e) => startResize(m, e)} onClick={(e) => e.stopPropagation()}
                      className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-[color:var(--brand-ocean)]/40" />}
                  </th>
                ))}
              </tr>
            )}
          </thead>
          <tbody>
            {virtual && vStart > 0 && <tr><td style={{ height: vStart * rowH }} colSpan={totalTdCols} /></tr>}
            {visibleRows.slice(vStart, vEnd).map((row, wi) => {
              const ri = vStart + wi;
              const isGrand = row.kind === "grand";
              const isGroup = row.kind === "group";
              const isSubtotal = row.kind === "subtotal";
              const showVals = !isGroup || row.collapsed || rowSubtotals;
              return (
                <tr key={`${row.kind}-${row.path.join(PSEP)}-${ri}`} data-testid={isGrand ? "pivot-grand-row" : isSubtotal ? "pivot-subtotal-row" : undefined}
                  style={hLineStyle || undefined}
                  className={"border-t border-[color:var(--glass-border)] " + (isGrand ? "border-t-2 bg-[color:var(--surface-2)]/70" : (isGroup || isSubtotal) ? "bg-[color:var(--surface-2)]/40" : (striped && ri % 2 === 1 ? "bg-[color:var(--surface-2)]/25 " : "") + (opts.hoverColor ? "" : "hover:bg-[color:var(--surface-2)]/60"))}>
                  {renderLabelCells(row, ri, row.kind === "leaf" ? prevLeafOf(ri) : null)}
                  {leafCols.map((lc, ci) => measureKeys.map((m) => renderValueTd(row, lc, m, `${ci}-${m}`, showVals, rowExtra(row, ri))))}
                </tr>
              );
            })}
            {virtual && vEnd < visibleRows.length && <tr><td style={{ height: (visibleRows.length - vEnd) * rowH }} colSpan={totalTdCols} /></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---- Batch 8 + Item 198: drag-and-drop grouping bar (Filters / Rows / Columns / Values) ----
function GroupingBar({ config, onConfigChange, dimMeta, measMeta, datasetId = null, rows = [], fieldFilters = {}, onFieldFilter = null }) {
  const { rowObjs, colObjs } = splitPivotDimObjects(config);
  const measures = config.measures || [];
  const dims = (config.dimensions || []).filter((d) => d && (d.key || d.field));
  const filters = config.filters || [];
  const dLabel = (d) => dimMeta[dimOutName(d)]?.label || d.label || d.key || d.field;
  const mLabel = (m) => measMeta[m.key || m.alias || `${m.agg || "sum"}_${m.field}`]?.label || m.key || `${m.agg || "sum"} of ${m.field}`;

  const setAxis = (dim, axis) => {
    onConfigChange({ dimensions: dims.map((d) => (d === dim || (dimOutName(d) === dimOutName(dim)) ? { ...d, axis } : (d.axis ? d : { ...d, axis: rowObjs.includes(d) ? "row" : "col" }))) });
  };
  const removeDim = (dim) => onConfigChange({ dimensions: dims.filter((d) => dimOutName(d) !== dimOutName(dim)) });
  const removeMeas = (i) => onConfigChange({ measures: measures.filter((_, j) => j !== i) });
  const reorderDim = (from, to) => {
    if (from === to) return;
    const arr = [...dims];
    const [it] = arr.splice(from, 1);
    arr.splice(to, 0, it);
    onConfigChange({ dimensions: arr });
  };

  const onDrop = (axis) => (e) => {
    e.preventDefault();
    try {
      const p = JSON.parse(e.dataTransfer.getData("application/x-pivot-chip") || "{}");
      if (p.kind === "dim") {
        const dim = dims[p.index];
        if (!dim) return;
        if (axis === "filter") {
          // Excel-style: move field from rows/cols into the report filter area
          if (!dim.field || dim.date_part) return;
          onConfigChange({
            dimensions: dims.filter((d) => dimOutName(d) !== dimOutName(dim)),
            filters: filters.some((f) => f.field === dim.field) ? filters : [...filters, { field: dim.field, op: "in", value: null }],
          });
        } else setAxis(dim, axis);
      }
    } catch { /* foreign drag */ }
  };
  const dragProps = (kind, index) => ({
    draggable: true,
    onDragStart: (e) => { e.dataTransfer.setData("application/x-pivot-chip", JSON.stringify({ kind, index })); e.dataTransfer.effectAllowed = "move"; },
  });

  const Chip = ({ label, onRemove, drag, testid }) => (
    <span data-testid={testid} {...(drag || {})}
      className="inline-flex items-center gap-0.5 pl-1 pr-1.5 py-0.5 rounded-md bg-[color:var(--surface)] border border-[color:var(--glass-border)] text-[10px] text-[color:var(--ink-2)] cursor-grab active:cursor-grabbing select-none max-w-[130px]">
      <GripVertical className="w-2.5 h-2.5 text-[color:var(--ink-3)] shrink-0" />
      <span className="truncate">{label}</span>
      {onRemove && <button onClick={onRemove} className="text-[color:var(--ink-3)] hover:text-[color:var(--danger)] shrink-0"><X className="w-2.5 h-2.5" /></button>}
    </span>
  );

  const Zone = ({ icon: Icon, title, children, axis, testid }) => (
    <div data-testid={testid} onDragOver={(e) => e.preventDefault()} onDrop={axis ? onDrop(axis) : undefined}
      className="flex items-center gap-1 flex-1 min-w-0 rounded-lg border border-dashed border-[color:var(--glass-border)] bg-[color:var(--surface-2)]/60 px-1.5 py-1 overflow-x-auto">
      <span className="flex items-center gap-0.5 text-[9px] font-semibold uppercase tracking-wide text-[color:var(--ink-3)] shrink-0"><Icon className="w-3 h-3" />{title}</span>
      {children}
    </div>
  );

  return (
    <div data-testid="pivot-grouping-bar" className="shrink-0 flex items-center gap-1.5 pb-1.5 flex-wrap">
      <Zone icon={FilterIcon} title="Filters" axis="filter" testid="pivot-zone-filters">
        {filters.map((f, i) => (
          <FilterChip key={`${f.field}-${i}`} f={f} datasetId={datasetId}
            onChange={(nf) => onConfigChange({ filters: filters.map((x, j) => (j === i ? nf : x)) })}
            onRemove={() => onConfigChange({ filters: filters.filter((_, j) => j !== i) })} />
        ))}
      </Zone>
      <Zone icon={Rows3} title="Rows" axis="row" testid="pivot-zone-rows">
        {rowObjs.map((d) => {
          const gi = dims.findIndex((x) => dimOutName(x) === dimOutName(d));
          const fn = dimOutName(d);
          return (
            <span key={fn} className="inline-flex items-center gap-0">
              <Chip testid={`pivot-chip-row-${fn}`} label={dLabel(d)} drag={dragProps("dim", gi)} onRemove={() => removeDim(d)} />
              {onFieldFilter && <PivotFieldFilter field={fn} rows={rows} ff={fieldFilters[fn]} onApply={(ff) => onFieldFilter(fn, ff)} />}
            </span>
          );
        })}
      </Zone>
      <Zone icon={Columns3} title="Cols" axis="col" testid="pivot-zone-cols">
        {colObjs.map((d) => {
          const gi = dims.findIndex((x) => dimOutName(x) === dimOutName(d));
          const fn = dimOutName(d);
          return (
            <span key={fn} className="inline-flex items-center gap-0">
              <Chip testid={`pivot-chip-col-${fn}`} label={dLabel(d)} drag={dragProps("dim", gi)} onRemove={() => removeDim(d)} />
              {onFieldFilter && <PivotFieldFilter field={fn} rows={rows} ff={fieldFilters[fn]} onApply={(ff) => onFieldFilter(fn, ff)} />}
            </span>
          );
        })}
      </Zone>
      <Zone icon={Sigma} title="Values" testid="pivot-zone-values">
        {measures.map((m, i) => (
          <Chip key={i} testid={`pivot-chip-meas-${i}`} label={mLabel(m)} onRemove={measures.length > 1 ? () => removeMeas(i) : null} />
        ))}
      </Zone>
      {datasetId && <FieldPicker datasetId={datasetId} config={config} onConfigChange={onConfigChange} />}
    </div>
  );
}

// Item 198.1/13: pivot report filter chip — pick allowed values from the field's distinct list.
function FilterChip({ f, datasetId, onChange, onRemove }) {
  const [options, setOptions] = useState(null);
  const [q, setQ] = useState("");
  const selected = Array.isArray(f.value) ? f.value : f.value != null ? [f.value] : [];
  const load = () => {
    if (options || !datasetId) return;
    api.post(`/datasets/${datasetId}/distinct`, { field: f.field, filters: [] })
      .then(({ data }) => setOptions(data.values || [])).catch(() => setOptions([]));
  };
  const toggleVal = (o) => {
    const set = new Set(selected);
    set.has(o) ? set.delete(o) : set.add(o);
    const arr = [...set];
    onChange({ ...f, op: "in", value: arr.length ? arr : null });
  };
  const shown = (options || []).filter((o) => !q || String(o).toLowerCase().includes(q.toLowerCase()));
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid={`pivot-filter-chip-${f.field}`} onClick={load}
          className="inline-flex items-center gap-1 pl-1.5 pr-1 py-0.5 rounded-md bg-[color:var(--surface)] border border-[color:var(--glass-border)] text-[10px] text-[color:var(--ink-2)] max-w-[150px]">
          <FilterIcon className="w-2.5 h-2.5 text-[color:var(--brand-ocean)] shrink-0" />
          <span className="truncate">{String(f.field).replace(/_/g, " ")}{selected.length ? `: ${selected.length}` : ": all"}</span>
          <span role="button" tabIndex={-1} onClick={(e) => { e.stopPropagation(); onRemove(); }} className="text-[color:var(--ink-3)] hover:text-[color:var(--danger)] shrink-0 cursor-pointer"><X className="w-2.5 h-2.5" /></span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 p-0">
        <div className="flex items-center justify-between px-3 py-2 border-b border-[color:var(--glass-border)]">
          <span className="text-xs font-medium text-[color:var(--ink-2)]">{String(f.field).replace(/_/g, " ")}</span>
          {selected.length > 0 && <button onClick={() => onChange({ ...f, value: null })} className="text-xs text-[color:var(--brand-ocean)]">All</button>}
        </div>
        {(options || []).length > 8 && (
          <div className="px-2 pt-2">
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="h-7 text-xs bg-[color:var(--surface-2)]" />
          </div>
        )}
        <div className="max-h-56 overflow-auto p-2 space-y-1">
          {options === null && <div className="text-xs text-[color:var(--ink-3)] px-2 py-3 text-center">Loading…</div>}
          {options !== null && shown.length === 0 && <div className="text-xs text-[color:var(--ink-3)] px-2 py-3 text-center">No values</div>}
          {shown.map((o) => (
            <label key={o} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-[color:var(--surface-2)] cursor-pointer">
              <Checkbox checked={selected.includes(o)} onCheckedChange={() => toggleVal(o)} />
              <span className="text-xs text-[color:var(--ink-2)] truncate">{String(o)}</span>
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Item 198.2: searchable field list — add any dataset field to the pivot in one click.
function FieldPicker({ datasetId, config, onConfigChange }) {
  const [schema, setSchema] = useState(null);
  const [q, setQ] = useState("");
  const load = () => {
    if (schema || !datasetId) return;
    api.get(`/datasets/${datasetId}`).then(({ data }) => setSchema(data.schema || [])).catch(() => setSchema([]));
  };
  const dims = config.dimensions || [];
  const measures = config.measures || [];
  const usedDim = new Set(dims.map((d) => d.field));
  const isNum = (t) => ["int", "float", "number", "int64", "float64", "numeric", "decimal"].some((k) => String(t || "").toLowerCase().includes(k));
  const addField = (col, as) => {
    if (as === "value") onConfigChange({ measures: [...measures, { field: col.name, agg: "sum" }] });
    else onConfigChange({ dimensions: [...dims, { field: col.name, axis: as }] });
  };
  const shown = (schema || []).filter((c) => !q || c.name.toLowerCase().includes(q.toLowerCase()));
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid="pivot-add-field-btn" title="Add field" onClick={load}
          className="shrink-0 p-1 rounded-md border border-dashed border-[color:var(--glass-border)] text-[color:var(--ink-3)] hover:text-[color:var(--brand-ocean)] hover:bg-[color:var(--surface-2)]">
          <Plus className="w-3.5 h-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-0">
        <div className="px-3 py-2 border-b border-[color:var(--glass-border)] text-xs font-medium text-[color:var(--ink-2)]">Add field to pivot</div>
        <div className="px-2 pt-2">
          <Input data-testid="pivot-field-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search fields…" className="h-7 text-xs bg-[color:var(--surface-2)]" />
        </div>
        <div className="max-h-64 overflow-auto p-2 space-y-0.5">
          {schema === null && <div className="text-xs text-[color:var(--ink-3)] px-2 py-3 text-center">Loading…</div>}
          {schema !== null && shown.length === 0 && <div className="text-xs text-[color:var(--ink-3)] px-2 py-3 text-center">No fields</div>}
          {shown.map((c) => (
            <div key={c.name} className="flex items-center gap-1 px-1.5 py-1 rounded hover:bg-[color:var(--surface-2)]">
              <span className="flex-1 text-xs text-[color:var(--ink-2)] truncate" title={c.name}>{c.name.replace(/_/g, " ")}</span>
              {!usedDim.has(c.name) && (
                <>
                  <button data-testid={`pivot-add-row-${c.name}`} onClick={() => addField(c, "row")} className="text-[9px] px-1 py-0.5 rounded bg-[color:var(--surface)] border border-[color:var(--glass-border)] text-[color:var(--ink-3)] hover:text-[color:var(--brand-ocean)]">Row</button>
                  <button onClick={() => addField(c, "col")} className="text-[9px] px-1 py-0.5 rounded bg-[color:var(--surface)] border border-[color:var(--glass-border)] text-[color:var(--ink-3)] hover:text-[color:var(--brand-ocean)]">Col</button>
                </>
              )}
              {isNum(c.dtype) && <button data-testid={`pivot-add-val-${c.name}`} onClick={() => addField(c, "value")} className="text-[9px] px-1 py-0.5 rounded bg-[color:var(--surface)] border border-[color:var(--glass-border)] text-[color:var(--ink-3)] hover:text-[color:var(--brand-ocean)]">Σ</button>}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Item 84: Excel-style per-field filter popup — member checkboxes + text/numeric operators.
const FF_TEXT_OPS = [["eq", "Equals"], ["ne", "Not equals"], ["contains", "Contains"], ["ncontains", "Does not contain"], ["starts", "Starts with"], ["ends", "Ends with"], ["blank", "Is blank"], ["nblank", "Is not blank"]];
const FF_NUM_OPS = [["num_eq", "="], ["num_ne", "≠"], ["num_gt", ">"], ["num_gte", "≥"], ["num_lt", "<"], ["num_lte", "≤"], ["between", "Between"], ["blank", "Is blank"], ["nblank", "Is not blank"]];

function PivotFieldFilter({ field, rows, ff, onApply }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [draft, setDraft] = useState(null);
  const uniques = useMemo(() => {
    const s = new Set();
    for (const r of rows) { s.add(String(r[field] ?? "\u2014")); if (s.size >= 500) break; }
    return [...s].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  }, [rows, field]);
  const numeric = useMemo(() => uniques.length > 0 && uniques.every((u) => u === "\u2014" || Number.isFinite(Number(u))), [uniques]);
  const active = !!ff && ((Array.isArray(ff.values) && ff.values.length > 0) || !!ff.op);
  const d = draft || { values: Array.isArray(ff?.values) ? [...ff.values] : [], op: ff?.op || "", opVal: ff?.opVal ?? "", opVal2: ff?.opVal2 ?? "" };
  const setD = (p) => setDraft({ ...d, ...p });
  const shown = q ? uniques.filter((u) => u.toLowerCase().includes(q.toLowerCase())) : uniques;
  const selSet = new Set(d.values);
  const toggleVal = (u) => { const s = new Set(selSet); s.has(u) ? s.delete(u) : s.add(u); setD({ values: [...s] }); };
  const apply = () => { onApply({ values: d.values.length ? d.values : null, op: d.op || null, opVal: d.opVal, opVal2: d.opVal2 }); setOpen(false); setDraft(null); };
  const clearAllFilter = () => { onApply(null); setOpen(false); setDraft(null); };
  const ops = numeric ? FF_NUM_OPS : FF_TEXT_OPS;
  const needsVal = d.op && !["blank", "nblank"].includes(d.op);
  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setDraft(null); }}>
      <PopoverTrigger asChild>
        <button data-testid={`pivot-field-filter-${field}`} title={`Filter ${field}`}
          className={"p-0.5 rounded shrink-0 " + (active ? "text-[color:var(--brand-ocean)]" : "text-[color:var(--ink-3)] hover:text-[color:var(--brand-ocean)]")}>
          <FilterIcon className={"w-2.5 h-2.5" + (active ? " fill-current" : "")} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-0" onOpenAutoFocus={(e) => e.preventDefault()}>
        <div className="px-3 py-2 border-b border-[color:var(--glass-border)] text-xs font-medium text-[color:var(--ink-2)]">{String(field).replace(/_/g, " ")}</div>
        <div className="px-2 pt-2 space-y-1.5">
          <div className="grid grid-cols-2 gap-1">
            <Select value={d.op || "__none__"} onValueChange={(v) => setD({ op: v === "__none__" ? "" : v })}>
              <SelectTrigger data-testid="pivot-ff-op" className="h-7 text-[11px] bg-[color:var(--surface-2)]"><SelectValue placeholder="Condition…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">No condition</SelectItem>
                {ops.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
            {needsVal && (
              <span className="flex gap-1">
                <Input data-testid="pivot-ff-opval" value={d.opVal} type={numeric && d.op !== "eq" ? "number" : "text"}
                  onChange={(e) => setD({ opVal: e.target.value })} placeholder="value" className="h-7 text-[11px] bg-[color:var(--surface-2)] min-w-0" />
                {d.op === "between" && <Input data-testid="pivot-ff-opval2" value={d.opVal2} type="number"
                  onChange={(e) => setD({ opVal2: e.target.value })} placeholder="and" className="h-7 text-[11px] bg-[color:var(--surface-2)] min-w-0" />}
              </span>
            )}
          </div>
          <Input data-testid="pivot-ff-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search values…" className="h-7 text-xs bg-[color:var(--surface-2)]" />
          <div className="flex gap-1 text-[10px]">
            <button data-testid="pivot-ff-select-all" onClick={() => setD({ values: shown })} className="px-1.5 py-0.5 rounded bg-[color:var(--surface-2)] text-[color:var(--ink-3)] hover:text-[color:var(--brand-ocean)]">Select all</button>
            <button data-testid="pivot-ff-clear-sel" onClick={() => setD({ values: [] })} className="px-1.5 py-0.5 rounded bg-[color:var(--surface-2)] text-[color:var(--ink-3)] hover:text-[color:var(--brand-ocean)]">Clear all</button>
            <button data-testid="pivot-ff-invert" onClick={() => setD({ values: uniques.filter((u) => !selSet.has(u)) })} className="px-1.5 py-0.5 rounded bg-[color:var(--surface-2)] text-[color:var(--ink-3)] hover:text-[color:var(--brand-ocean)]">Invert</button>
          </div>
        </div>
        <div className="max-h-48 overflow-auto p-2 space-y-0.5">
          {shown.length === 0 && <div className="text-xs text-[color:var(--ink-3)] px-2 py-2 text-center">No values</div>}
          {shown.map((u) => (
            <label key={u} data-testid="pivot-ff-value-row" className="flex items-center gap-2 px-2 py-0.5 rounded hover:bg-[color:var(--surface-2)] cursor-pointer">
              <Checkbox checked={selSet.has(u)} onCheckedChange={() => toggleVal(u)} />
              <span className="text-xs text-[color:var(--ink-2)] truncate">{u}</span>
            </label>
          ))}
        </div>
        <div className="flex items-center justify-between gap-1 px-2 py-2 border-t border-[color:var(--glass-border)]">
          <button data-testid="pivot-ff-clear" onClick={clearAllFilter} className="text-[11px] text-[color:var(--danger)] hover:underline">Clear filter</button>
          <span className="flex gap-1">
            <button data-testid="pivot-ff-cancel" onClick={() => { setOpen(false); setDraft(null); }} className="text-[11px] px-2 py-1 rounded bg-[color:var(--surface-2)] text-[color:var(--ink-3)]">Cancel</button>
            <button data-testid="pivot-ff-apply" onClick={apply} className="text-[11px] px-2.5 py-1 rounded bg-[color:var(--brand-ocean)] text-white hover:opacity-90">Apply</button>
          </span>
        </div>
      </PopoverContent>
    </Popover>
  );
}
